<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            <!-- Title Field -->
            <div class="col-12">
              <label for="title" class="bmd-label-float">{{ $t('announcement.title') }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.title }"
                id="title"
                v-model.trim="formData.title"
                required
                @blur="validateField('title')"
              />
              <div class="invalid-feedback">{{ errors.title }}</div>
            </div>

            <!-- Description Field -->
            <div class="col-12">
              <label for="description" class="bmd-label-float">{{ $t('announcement.description') }}</label>
              <textarea
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.description }"
                id="description"
                v-model.trim="formData.description"
                required
                rows="4"
                @blur="validateField('description')"
              ></textarea>
              <div class="invalid-feedback">{{ errors.description }}</div>
            </div>

            <!-- Date Field -->
            <div class="col-md-6">
              <label for="date" class="bmd-label-float">{{ $t('announcement.date') }}</label>
              <input
                type="datetime-local"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.date }"
                id="date"
                v-model="formData.date"
                required
                @blur="validateField('date')"
              />
              <div class="invalid-feedback">{{ errors.date }}</div>
            </div>

            <!-- Status Field -->
            <div class="col-md-6">
              <label for="status" class="bmd-label-float">{{ $t('announcement.status') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.status }"
                id="status"
                v-model="formData.status"
                required
                @blur="validateField('status')"
              >
                <option value="active">{{ $t('announcement.statusActive') }}</option>
                <option value="inactive">{{ $t('announcement.statusInactive') }}</option>
              </select>
              <div class="invalid-feedback">{{ errors.status }}</div>
            </div>

            <!-- Audience Selection -->
            <div class="col-md-4">
              <label class="bmd-label-float">{{ $t('announcement.audience') }}</label>
              <select class="form-select borderless-input"
                      v-model="formData.audience"
                      @change="handleAudienceChange">
                <option value="all">{{ $t('announcement.audienceAll') }}</option>
                <option value="class">{{ $t('announcement.audienceClass') }}</option>
                <option value="group">{{ $t('announcement.audienceGroup') }}</option>
              </select>
            </div>

            <!-- Class Selection -->
            <div class="col-md-4" v-if="formData.audience === 'class' || formData.audience === 'group'">
              <label class="bmd-label-float">{{ $t('announcement.class') }}</label>
              <select class="form-select borderless-input" 
                      v-model.number="formData.class_id" 
                      @change="onClassChange"
                      :class="{ 'is-invalid': errors.class_id }">
                <option :value="null" disabled>{{ $t('common.select') }}</option>
                <option v-for="c in classes" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.class_id }}</div>
              
              <!-- Display Selected Class -->
              <div v-if="formData.audience === 'class' && formData.class_id" class="mt-2">
                <span class="badge bg-info d-inline-flex align-items-center">
                  <i class="fas fa-check me-1"></i>
                  {{ $t('announcement.selectedClass') }}: {{ getClassName(formData.class_id) }}
                </span>
              </div>
            </div>

            <!-- Group Selection -->
            <div class="col-md-4" v-if="formData.audience === 'group'">
              <label class="bmd-label-float">{{ $t('announcement.group') }}</label>
              <select class="form-select borderless-input" 
                      v-model.number="formData.class_group_id" 
                      @change="onGroupChange"
                      :class="{ 'is-invalid': errors.class_group_id }">
                <option :value="null" disabled>{{ $t('common.select') }}</option>
                <option v-for="g in groups" :key="g.id" :value="g.id">
                  {{ g.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.class_group_id }}</div>
              
              <!-- Display Selected Class and Group -->
              <div v-if="formData.audience === 'group' && formData.class_id && formData.class_group_id" class="mt-2">
                <div class="d-flex flex-wrap gap-2">
                  <span class="badge bg-info d-inline-flex align-items-center">
                    <i class="fas fa-check me-1"></i>
                    {{ $t('announcement.class') }}: {{ getClassName(formData.class_id) }}
                  </span>
                  <span class="badge bg-success d-inline-flex align-items-center">
                    <i class="fas fa-check me-1"></i>
                    {{ $t('announcement.group') }}: {{ getGroupName(formData.class_group_id) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Image Field -->
            <div class="col-12">
              <label for="image" class="bmd-label-float">{{ $t('announcement.image') }}</label>
              <div
                class="card dashed-border p-3 text-center cursor-pointer position-relative"
                @click="$refs.fileInput.click()"
              >
                <input
                  type="file"
                  ref="fileInput"
                  id="image"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload"
                />

                <div v-if="loadingImage" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ $t('announcement.loading') }}</span>
                  </div>
                </div>

                <img
                  v-else-if="previewImage || (isEditMode && initialData.image)"
                  :src="previewImage || initialData.image"
                  class="img-fluid mx-auto d-block"
                  style="max-height: 200px"
                />

                <div v-else class="py-4">
                  <i class="fas fa-image fs-1 text-muted"></i>
                  <p class="text-muted mt-2 mb-0">{{ $t('announcement.clickToUpload') }}</p>
                </div>

                <button
                  v-if="previewImage || (isEditMode && initialData.image)"
                  @click.stop="clearImage"
                  class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
              <small class="text-muted d-block mt-1">{{ $t('announcement.imageNote') }}</small>
              <div class="invalid-feedback">{{ errors.image }}</div>
            </div>

          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineEmits, defineProps, defineExpose, nextTick } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';

const { t } = useI18n();
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      title: "",
      description: "",
      image: null,
      date: "",
      status: "active",
      audience: "all",
      class_id: null,
      class_group_id: null,
    }),
  },
  isEditMode: Boolean
});

const emit = defineEmits(['submit', 'validation-error', 'update:form-data']);
const toast = useToast();

const classes = ref([]);
const groups = ref([]);
const fileInput = ref(null);
const loadingImage = ref(false);
const previewImage = ref(null);
const isInitialized = ref(false);

const formData = ref({
  title: "",
  description: "",
  image: null,
  date: "",
  status: "active",
  audience: "all",
  class_id: null,
  class_group_id: null,
});

const errors = ref({});

// Helper methods
const getClassName = (classId) => {
  const classObj = classes.value.find(c => c.id === classId);
  return classObj ? classObj.name : 'Unknown Class';
};

const getGroupName = (groupId) => {
  const groupObj = groups.value.find(g => g.id === groupId);
  return groupObj ? groupObj.name : 'Unknown Group';
};

// Event handlers
const handleAudienceChange = () => {
  if (formData.value.audience === 'all') {
    formData.value.class_id = null;
    formData.value.class_group_id = null;
    groups.value = [];
  } else if (formData.value.audience === 'class') {
    formData.value.class_group_id = null;
    groups.value = [];
  }
  validateAudienceFields();
};

const onClassChange = () => {
  if (formData.value.audience === 'group') {
    formData.value.class_group_id = null;
    loadGroupsForClass(formData.value.class_id);
  }
  validateAudienceFields();
};

const onGroupChange = () => {
  validateAudienceFields();
};

// Validation
const validateField = (field) => {
  delete errors.value[field];

  if (field === 'date') {
    const val = formData.value.date;
    const invalid = !val || isNaN(new Date(val).getTime());
    if (invalid) {
      errors.value.date = t('announcement.invalidDate');
      toast.error(t('announcement.invalidDate'));
      return false;
    }
    return true;
  }

  const val = formData.value[field];
  if (!val) {
    errors.value[field] = t('announcement.required');
    toast.error(t('announcement.required'));
    return false;
  }

  return true;
};

const validateAudienceFields = () => {
  delete errors.value.class_id;
  delete errors.value.class_group_id;

  if (formData.value.audience === 'class' && !formData.value.class_id) {
    errors.value.class_id = t('announcement.classRequired');
    return false;
  }

  if (formData.value.audience === 'group') {
    if (!formData.value.class_id) {
      errors.value.class_id = t('announcement.classRequired');
      return false;
    }
    if (!formData.value.class_group_id) {
      errors.value.class_group_id = t('announcement.groupRequired');
      return false;
    }
  }

  return true;
};

const validateForm = () => {
  errors.value = {};

  const checks = [
    validateField('title'),
    validateField('description'),
    validateField('date'),
    validateField('status'),
    validateAudienceFields(),
  ];

  if (!props.isEditMode && !formData.value.image) {
    errors.value.image = t('announcement.requiredImage');
    toast.error(t('announcement.requiredImage'));
    checks.push(false);
  }

  return checks.every(Boolean);
};

// Image handling
const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return clearImage();

  const validTypes = ["image/jpeg","image/png","image/jpg","image/gif"];
  if (!validTypes.includes(file.type.toLowerCase()) || file.size > 2*1024*1024) {
    toast.error(t('announcement.invalidImage'));
    clearImage();
    return;
  }

  formData.value.image = file;
  const reader = new FileReader();
  reader.onload = e => previewImage.value = e.target.result;
  reader.readAsDataURL(file);
};

const clearImage = () => {
  formData.value.image = null;
  previewImage.value = null;
  if(fileInput.value) fileInput.value.value = "";
};

// Form submission
const handleSubmit = () => {
  if (!validateForm()) {
    emit("validation-error");
    toast.warning(t("announcement.fixErrors"));
    return;
  }

  const fd = new FormData();

  if (formData.value.image instanceof File) {
    fd.append('image', formData.value.image);
  }

  fd.append('title', formData.value.title);
  fd.append('description', formData.value.description);
  fd.append('date', formData.value.date);
  fd.append('status', formData.value.status);

  fd.append('audience_type', formData.value.audience);

 fd.append('class_id', formData.value.class_id ?? '');
  fd.append('class_group_id', formData.value.class_group_id ?? '');

  emit('submit', fd);
};

const submitForm = () => {
  handleSubmit();
};

// Data loading
const loadClasses = async () => {
  try {
    const { data } = await api.get('/classes', { params: { per_page: 1000 } });
    classes.value = (data?.data || data || []).map(c => ({ id: c.id, name: c.name }));
    console.log('Loaded classes:', classes.value);
  } catch (error) {
    console.error('Failed to load classes:', error);
    toast.error(t('announcement.loadClassesError'));
  }
};

const loadGroupsForClass = async (classId) => {
  groups.value = [];
  if (!classId) return;
  
  try {
    const { data } = await api.get(`/classes/${classId}/groups`);
    let groupsData = data?.data || data || [];
    
    groups.value = groupsData.map(g => ({ 
      id: g.id, 
      name: g.name || `Group #${g.id}` 
    }));
    
  } catch (error) {
    toast.error(t('announcement.loadGroupsError'));
  }
};

// Initialization
const initializeFormData = async () => {
  
  formData.value = {
    title: props.initialData.title ?? "",
    description: props.initialData.description ?? "",
    image: null,
    date: props.initialData.date
      ? new Date(props.initialData.date).toISOString().slice(0,16)
      : new Date().toISOString().slice(0,16),
    status: props.initialData.status ?? "active",
    audience: props.initialData.audience_type ?? "all",
    class_id: props.initialData.class_id ?? props.initialData.class ?? null,
    class_group_id: props.initialData.class_group_id ?? props.initialData.group ?? null,
  };


  if (props.initialData.image) {
    previewImage.value = props.initialData.image.startsWith('http')
      ? props.initialData.image
      : `/storage/${props.initialData.image}`;
  }

  // Load groups if editing with group audience
  if (props.isEditMode && formData.value.audience === 'group' && formData.value.class_id) {
    await loadGroupsForClass(formData.value.class_id);
    await nextTick(); // Wait for Vue to update
    
    // After groups are loaded, ensure the selected group is properly set
    if (formData.value.class_group_id) {
      
      // Check if the selected group exists in the loaded groups
      const selectedGroupExists = groups.value.some(g => g.id === formData.value.class_group_id);
      if (!selectedGroupExists) {
        formData.value.class_group_id = null;
      }
    }
  }
  
  isInitialized.value = true;
  console.log('=== FORM INITIALIZATION COMPLETE ===');
};

// Watchers
watch(() => formData.value.class_id, (cid) => {
  if (formData.value.audience === 'group' && isInitialized.value) {
    formData.value.class_group_id = null;
    loadGroupsForClass(cid);
  }
});

watch(() => formData.value.audience, (a) => {
  if (a === 'all') { 
    formData.value.class_id = null; 
    formData.value.class_group_id = null; 
  }
  if (a === 'class') { 
    formData.value.class_group_id = null; 
  }
});

// Lifecycle
onMounted(async () => {
  await loadClasses(); 
  await initializeFormData(); 
});

watch(() => props.initialData, async () => {
  if (props.initialData) {
    await initializeFormData();
  }
}, { deep: true });

watch(() => formData.value, newVal => emit('update:form-data', newVal), { deep: true, immediate: true });

defineExpose({ submitForm, formData });
</script>

<style scoped>
.dashed-border {
  border: 2px dashed #dee2e6;
  transition: border-color 0.3s;
}

.dashed-border:hover {
  border-color: #adb5bd;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}
</style>